import Grid from '@mui/material/Grid';
import Heading from '@/components/Heading/Heading';
import type { HighlightIconCardContent } from '@/components/HighlightIconStat/HighlightIconStat';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import {
  type Contentful,
  ContentStyle,
  type FrontifyImageContent,
} from '@/contentful/contentful-exports';
import useViewPortSize from '@/hooks/useViewportSize';
import { ViewportSize } from '@/styles/viewportSize';
import { setImgWidth, setSrcSetImg } from '@/utils/frontify';
import useStyles from './GlobalList.styles';
import FeatureList from './variants/FeatureList';
import IconList from './variants/IconList';
import ImageList from './variants/ImageList';
import TextList from './variants/TextList';

export interface GlobalListImageCardContent extends Contentful {
  fields: {
    image?: FrontifyImageContent[];
    heading?: string;
    body?: string;
    chipText?: string;
  };
}
export interface GlobalListTextCardContent extends Contentful {
  fields: {
    heading?: string;
    body?: string;
  };
}

export interface GlobalListContent extends Contentful {
  fields: {
    style: ContentStyle;
    eyebrow: string;
    heading: string;
    subheading?: string;
    leadImageLarge: FrontifyImageContent[];
    leadImageSmall: FrontifyImageContent[];
    imageListHeading?: string;
    imageList?: GlobalListImageCardContent[];
    iconListHeading?: string;
    iconList?: HighlightIconCardContent[];
    textListHeading?: string;
    textList?: GlobalListTextCardContent[];
    featureListHeading?: string;
    featureList?: string[];
    listLeadImageAltText?: string;
  };
}

interface GlobalListProps {
  content: GlobalListContent;
}

export default function GlobalList({
  content: {
    fields: {
      style,
      eyebrow,
      heading,
      subheading,
      leadImageLarge,
      leadImageSmall,
      imageListHeading,
      iconListHeading,
      textListHeading,
      featureListHeading,
      textList,
      featureList,
      iconList,
      imageList,
      listLeadImageAltText,
    },
  },
}: GlobalListProps) {
  const isOnDark = style === ContentStyle.OnDark;
  const { classes, cx } = useStyles({ isOnDark });
  const { isExtraSmall, isSmall } = useViewPortSize();
  const isMobile = isExtraSmall || isSmall;
  const leadImage = isMobile ? leadImageSmall : leadImageLarge;
  const imageSize = isMobile ? ViewportSize.md : ViewportSize.lg;

  const img =
    leadImage && leadImage.length > 0
      ? setImgWidth(leadImage[0].preview_url, imageSize)
      : null;

  // biome-ignore lint/correctness/noNestedComponentDefinitions: closes over per-render props
  // biome-ignore lint/correctness/noComponentHookFactories: closes over per-render props
  const ListHeading = ({
    text,
    border = true,
  }: {
    text: string;
    border?: boolean;
  }) => {
    return (
      <GridContainer
        fixed
        gridProps={{
          className: cx(classes.listHeading, border && classes.headingBorder),
        }}
      >
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Heading level='h3'>{text}</Heading>
        </Grid>
      </GridContainer>
    );
  };

  return (
    <SpacingContainer className={classes.container}>
      <GridContainer fixed>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <SectionHeader
            fixed
            isOnDark={isOnDark}
            eyebrow={eyebrow}
            heading={heading}
            subheading={subheading}
          />
        </Grid>
      </GridContainer>
      <GridContainer fixed>
        {img && (
          <Grid size={12}>
            {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
            <img
              src={img}
              srcSet={setSrcSetImg(leadImage[0].preview_url, imageSize)}
              className={classes.leadImage}
              alt={listLeadImageAltText}
            />
          </Grid>
        )}
      </GridContainer>
      {imageListHeading && (
        <div className={classes.listContainer}>
          {imageListHeading && <ListHeading text={imageListHeading} />}
          <ImageList list={imageList} isOnDark={isOnDark} />
        </div>
      )}
      {iconList && (
        <div className={classes.listContainer}>
          {iconListHeading && <ListHeading text={iconListHeading} />}
          <IconList list={iconList} isOnDark={isOnDark} />
        </div>
      )}
      {textList && (
        <div className={classes.listContainer}>
          {textListHeading && <ListHeading text={textListHeading} />}
          <TextList list={textList} isOnDark={isOnDark} />
        </div>
      )}
      {featureList && (
        <div className={classes.listContainer}>
          {featureListHeading && (
            <ListHeading text={featureListHeading} border={false} />
          )}
          <FeatureList list={featureList} isOnDark={isOnDark} />
        </div>
      )}
    </SpacingContainer>
  );
}
